import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import useQueryParams from 'hooks/UseQueryParams';
import { PROVINCE } from 'pages/report/CreditConstants';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ColorList from 'sharedComponents/commonHelper/ColorList';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { getToken } from 'store/CommonModuleState';
import { blackbookVehicleOptions, vehicleColorsLookup } from 'store/TradeInState';
import { RootState } from 'store/store';
import { colorStyles } from '../TradeInConstant';
import Logger from 'utils/logger';

const VehicleInfo: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  const hasCalledNextStep = React.useRef(false);
  const hasRendered = React.useRef(false);
  const hasCalledTokenApi = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { vehicleColorData, vehicleOptions } = useSelector((state: RootState) => state.tradeInStore);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const dealershipId = widgetData?.dealershipId ?? widgetData?.dealershipName;

  const { token } = useSelector((state: RootState) => state.commonModuleStore);

  const year = queryParams.get('year');
  const make = queryParams.get('make');
  const model = queryParams.get('model');
  const trim = queryParams.get('trim');
  const heading = queryParams.get('heading');
  const uvc = queryParams.get('uvc');
  const type = queryParams.get('type');

  const { control, setValue, trigger, getValues } = useFormContext();

  const headingWatcher = useWatch({
    control,
    name: 'heading',
  }) as string;
  const provinceWatcher = useWatch({
    control,
    name: 'province',
  }) as string;

  useEffect(() => {
    const vehicleSearch = getValues('vehicleSearch') || {};

    if (
      authenticateClientData &&
      Object.keys(authenticateClientData).length > 0 &&
      !token &&
      (uvc || vehicleSearch.uvc)
    ) {
      if (hasCalledTokenApi.current) return;
      hasCalledTokenApi.current = true;

      dispatch(getToken(dealershipId));
    }
  }, [authenticateClientData]);

  useEffect(() => {
    setTimeout(() => {
      const vehicleSearch = getValues('vehicleSearch') || {};
      if (uvc || vehicleSearch.uvc) {
        if (hasRendered.current) return;
        hasRendered.current = true;

        setValue('year', year || vehicleSearch.year || '');
        setValue('make', make || vehicleSearch.make || '');
        setValue('model', model || vehicleSearch.model || '');
        setValue('trim', trim || vehicleSearch.trim || '');
        setValue('heading', heading || vehicleSearch.heading || '');
        setValue('uvc', uvc || vehicleSearch.uvc || '');

        // dispatch(vehicleColorsLookup(uvc || vehicleSearch.uvc));

        dispatch(
          vehicleColorsLookup({
            UVC: uvc || vehicleSearch.uvc,
          })
        );

        dispatch(
          blackbookVehicleOptions({
            UVC: uvc || vehicleSearch.uvc,
          })
        );

        Logger.custom('USER_SEARCH_VEHICLE_INFO', {
          uvc: uvc || vehicleSearch.uvc,
          vehicle: heading || vehicleSearch.heading,
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Trade-In',
        });
      }
    });
  }, [year, make, model, trim, heading, uvc, token, dealershipId, setValue, getValues, dispatch]);

  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    onNext?.(vehicleOptions?.length > 0 ? (type === 'banner' ? 1 : 2) : type === 'banner' ? 2 : 3);
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  const exteriorColors = vehicleColorData?.find((category) => category.name === 'Exterior Colours')?.color_list || [];
  // const leatherColors = vehicleColorData?.find((category) => category.name === 'Leather Colours')?.color_list || []; //  Temporarily removed
  const interiorColors = vehicleColorData?.find((category) => category.name === 'Interior Colours')?.color_list || [];

  return (
    <>
      <div className='tradein-v3__body'>
        <h4 className='tradein-v3__body__title text-primary'>{t('stepperFormTradeIn.vehicleInformation')}</h4>
        <div className='tradein-v3__body__vd'>
          <label className='tradein-v3__body__vd__lbl'>{t('stepperFormTradeIn.yourVehicle')}</label>
          <div className='tradein-v3__body__vd__name'>{headingWatcher}</div>
        </div>

        {exteriorColors && exteriorColors.length > 0 ? (
          <div className='tradein-v3__body__ext-color mb-3'>
            <label className='tradein-v3__body__ext-color__title'>{t('stepperFormTradeIn.exteriorColour')}</label>
            <ColorList
              colorList={exteriorColors}
              control={control}
              trigger={trigger}
              name='exteriorColor'
              t={t}
              styles={colorStyles}
            />
          </div>
        ) : null}

        {interiorColors && interiorColors.length > 0 ? (
          <div className='tradein-v3__body__ext-color mb-3'>
            <label className='tradein-v3__body__ext-color__title'>{t('stepperFormTradeIn.interiorColour')}</label>
            <ColorList
              colorList={interiorColors}
              control={control}
              trigger={trigger}
              name='interiorColor'
              t={t}
              styles={colorStyles}
            />
          </div>
        ) : null}

        <div className='form-group'>
          <CommonInput
            name='mileage'
            control={control}
            label={t('stepperFormTradeIn.vehicleMileage')}
            placeholder={t('Enter')}
            rules={formRequiredRules(errorMessage(t).vehicleMileageRequired)}
            triggerValidationForField={trigger}
            hint={t('stepperFormTradeIn.vehicleMileageNote')}
            type='number'
          />
        </div>

        <div className='form-group'>
          <SelectInput
            name='province'
            control={control}
            label={t('stepperForm.province')}
            rules={formRequiredRules(errorMessage(t).provinceRequired)}
            options={PROVINCE}
            triggerValidationForField={trigger}
            defaultValue={provinceWatcher}
          />
        </div>
      </div>
    </>
  );
};

const VehicleInfoStep = React.forwardRef(VehicleInfo);

export default VehicleInfoStep;
